import { Card, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { MdPerson } from 'react-icons/md';
import { TextValueField } from '../../../common/TextValueField';
import { useSuIntl } from '../../../../hooks/useSuIntl';
import { useGetUserProfileDetail } from '../../../../query/resource-hooks/company';
import { useGetUserCustomFields } from '../../../../query/resource-hooks/customFields';
import { UserCustomFieldValue } from '../../../../types/customFields';
import { useEffect } from 'react';

// Field names to display in the card
const DISPLAYED_FIELD_NAMES = ['HOUR_RATE', 'DAY_RATE', 'LEGAL_RELATIONSHIP', 'JOB_POSITION'];

type UserDetailsCardProps = {
    userId: number;
    userName?: string;
    onCustomFieldsLoaded?: (fields: UserCustomFieldValue[]) => void;
};

export const UserDetailsCard: React.FC<UserDetailsCardProps> = ({ userId, userName, onCustomFieldsLoaded }) => {
    const { t } = useSuIntl();

    const { data: user, isLoading: isUserLoading } = useGetUserProfileDetail(userId);
    const { data: customFieldsData, isLoading: isFieldsLoading } = useGetUserCustomFields(userId, !!userId);

    // Filter to show only specific fields that have values
    const displayedFields = customFieldsData?.data?.filter(
        (field) => DISPLAYED_FIELD_NAMES.includes(field.fieldName) && field.formattedValue !== null && field.formattedValue !== ''
    );

    // Notify parent when custom fields are loaded (for future use in statements)
    useEffect(() => {
        if (customFieldsData?.data && onCustomFieldsLoaded) {
            onCustomFieldsLoaded(customFieldsData.data);
        }
    }, [customFieldsData?.data, onCustomFieldsLoaded]);

    const isLoading = isUserLoading || isFieldsLoading;

    if (isLoading) {
        return (
            <Card p={4} boxShadow={'md'} minW={270}>
                <HStack justify={'space-between'} align={'center'} mb={4}>
                    <Text fontSize={'lg'} fontWeight={'500'}>
                        {t('tab.employees.detail.title')}
                    </Text>
                    <MdPerson size={25} />
                </HStack>
                <VStack spacing={2} align={'stretch'}>
                    <Skeleton height={6} borderRadius={'md'} />
                    <Skeleton height={6} borderRadius={'md'} />
                    <Skeleton height={6} borderRadius={'md'} />
                </VStack>
            </Card>
        );
    }

    const displayName = user?.fullName || userName || '-';
    const role = user?.role ? t(user.role) : '-';

    return (
        <Card p={4} boxShadow={'md'} minW={270}>
            <HStack justify={'space-between'} align={'center'} mb={4}>
                <Text fontSize={'lg'} fontWeight={'500'}>
                    {displayName}
                </Text>
                <MdPerson size={25} />
            </HStack>
            <VStack spacing={2} align={'stretch'}>
                <TextValueField smallVariant label={t('role')} value={role} />
                {displayedFields?.map((field) => (
                    <TextValueField key={field.id} smallVariant label={field.displayName} value={field.formattedValue || '-'} />
                ))}
            </VStack>
        </Card>
    );
};

export default UserDetailsCard;
